import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, Drawer, Field, FieldLabel, Input, Select } from "../components/ui";
import { CostAcknowledgementGate } from "../components/CostAcknowledgementGate";
import { DeskTargetGate } from "../components/DeskTargetGate";
import { TargetJobRunsPanel } from "../components/TargetJobRunsPanel";
import { useToast } from "../components/Toast";
import { useDeskSpend } from "../context/DeskSpendContext";
import { useDraftState } from "../context/DraftStateContext";
import { apiGet, apiPost } from "../lib/api";
import { jobIdFromPhase2State, resolveJobForDeskTarget } from "../lib/deskTarget";
import type { Phase1JobPoll } from "../lib/phase1Job";
import type {
  ContractorGroup,
  EvidencePage as EvidencePageRow,
  Phase2State,
} from "../lib/types";
import { formatApiError, setStoredJobId } from "../lib/utils";

const PAGE_BATCH = 10;

function dedupeContractorSources(
  sources: NonNullable<ContractorGroup["sources"]>,
): NonNullable<ContractorGroup["sources"]> {
  const seen = new Set<string>();
  const out: NonNullable<ContractorGroup["sources"]> = [];
  for (const s of sources) {
    const key = (s.url || s.evidence_id || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function formatPublishedDate(raw: string | undefined): string {
  const value = (raw || "").trim();
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type TargetOption = {
  target_display_name: string;
  latest_job_id?: string;
  evidence_count?: number;
  extracted_count?: number;
  pending_extraction_count?: number;
};

function formatTargetOptionLabel(t: TargetOption): string {
  const name = t.target_display_name;
  const total = t.evidence_count ?? 0;
  const pending = t.pending_extraction_count ?? 0;
  const extracted =
    t.extracted_count ??
    (total > 0 && pending >= 0 ? Math.max(0, total - pending) : 0);
  if (total <= 0) {
    return name;
  }
  const progress = `${extracted}/${total} extracted`;
  if (pending > 0) {
    return `${name} (${progress} · ${pending} pending)`;
  }
  return `${name} (${progress})`;
}

function EvidencePageLinks({ pages }: { pages: EvidencePageRow[] }) {
  if (!pages.length) {
    return (
      <p className="px-6 py-4 text-body-sm text-on-surface-variant">No pages match your filter.</p>
    );
  }
  return (
    <ul className="divide-y divide-outline-variant/60">
      {pages.map((p) => (
        <li key={p.evidence_id || p.url}>
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-6 py-3 hover:bg-surface-variant/40 text-left"
          >
            <span className="text-primary font-data-mono text-body-sm font-semibold line-clamp-2">
              {p.title || p.url}
            </span>
            <p className="text-[10px] text-on-surface-variant truncate mt-1">{p.url}</p>
            <div className="flex items-center gap-2 mt-1">
              {p.source ? (
                <p className="text-[10px] text-tertiary">{p.source}</p>
              ) : null}
              {formatPublishedDate(p.published_date) ? (
                <p className="text-[10px] text-on-surface-variant">
                  {formatPublishedDate(p.published_date)}
                </p>
              ) : null}
            </div>
          </a>
        </li>
      ))}
    </ul>
  );
}

export function EvidencePage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { draft } = useDraftState();
  const [params, setParams] = useSearchParams();
  const runParam = params.get("run") || "";
  const jobParam = params.get("job_id") || params.get("job") || "";
  const targetParam = params.get("target") || "";
  const deskTarget = draft.target_company.trim();
  const effectiveTarget = (targetParam || deskTarget).trim();
  const [jobId, setJobId] = useState(() => {
    if (params.get("run")) return "";
    return params.get("job_id") || params.get("job") || "";
  });
  const [state, setState] = useState<Phase2State | null>(null);
  const [targets, setTargets] = useState<TargetOption[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [pageFilter, setPageFilter] = useState("");
  const [model, setModel] = useState("");
  const [force, setForce] = useState(false);
  const [costAckReady, setCostAckReady] = useState(false);
  const { spend, refresh: refreshSpend } = useDeskSpend();
  const [extracting, setExtracting] = useState(false);
  const [extractRunId, setExtractRunId] = useState("");
  const [extractLogUrl, setExtractLogUrl] = useState("");
  const [extractProgress, setExtractProgress] = useState<Phase1JobPoll["progress"] | null>(
    null,
  );
  const [pageBatch, setPageBatch] = useState(0);
  const [extractCollapsed, setExtractCollapsed] = useState(false);
  const [pagesCollapsed, setPagesCollapsed] = useState(true);
  const [contractorsCollapsed, setContractorsCollapsed] = useState(false);
  const [mergeIntoSelect, setMergeIntoSelect] = useState("");
  const [mergeIntoCustom, setMergeIntoCustom] = useState("");
  const [contractorActionBusy, setContractorActionBusy] = useState(false);
  const pollRef = useRef<number | null>(null);
  const extractRunIdRef = useRef("");
  const [jobsRefreshToken, setJobsRefreshToken] = useState(0);
  const [jobsPanelOpen, setJobsPanelOpen] = useState(false);
  const [pageGate, setPageGate] = useState<"loading" | "ready" | "no_target" | "no_job">(
    "loading",
  );
  const [pendingTarget, setPendingTarget] = useState("");

  useEffect(() => {
    if (runParam) {
      navigate(`/search?run=${encodeURIComponent(runParam)}`, { replace: true });
    }
  }, [runParam, navigate]);

  useEffect(() => {
    setPageBatch(0);
  }, [jobId, pageFilter]);

  const stopPoll = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const applyPhase2State = useCallback((s: Phase2State) => {
    setState(s);
    const canonical = s.canonical_job_id;
    if (canonical) {
      setJobId(canonical);
      setStoredJobId(canonical);
    }
    setModel(
      String(
        s.default_extraction_model ||
          (s as { workspace?: { phase2_extraction_model?: string } }).workspace
            ?.phase2_extraction_model ||
          s.cheap_models?.[0]?.id ||
          "",
      ),
    );
    const groups = s.contractor_groups || [];
    if (groups.length) {
      setSelected((prev) =>
        prev && groups.some((g) => g.name === prev) ? prev : null,
      );
    } else {
      setSelected(null);
    }
  }, []);

  const loadPhase2State = useCallback(
    async (jid: string) => {
      const data = await apiGet<{ state: Phase2State }>(
        `/api/phase2/state?job_id=${encodeURIComponent(jid)}`,
      );
      applyPhase2State(data.state);
      setTargets((data.state.targets as TargetOption[]) || []);
      const canonical = data.state.canonical_job_id || jid;
      if (canonical !== jid) {
        setParams({ job_id: canonical });
      }
      const merged = Number(data.state.merged_urls || 0);
      if (merged > 0) {
        toast(`Merged ${merged} page(s) from earlier runs for this company`);
      }
      return data.state;
    },
    [applyPhase2State, setParams, toast],
  );

  const loadJobContext = useCallback(
    async (jid: string) => {
      await loadPhase2State(jid);
    },
    [loadPhase2State],
  );

  useEffect(() => {
    if (runParam) return;

    let cancelled = false;

    const bootstrap = async () => {
      setPageGate("loading");

      if (jobParam) {
        setJobId(jobParam);
        try {
          await loadJobContext(jobParam);
          if (!cancelled) setPageGate("ready");
        } catch (e) {
          if (!cancelled) {
            toast(formatApiError(e), true);
            setPageGate("ready");
          }
        }
        return;
      }

      if (!effectiveTarget) {
        setPageGate("no_target");
        return;
      }

      setPendingTarget(effectiveTarget);
      if (!targetParam && deskTarget) {
        setParams({ target: deskTarget }, { replace: true });
      }

      const resolved = await resolveJobForDeskTarget(effectiveTarget);
      if (cancelled) return;

      if (resolved) {
        setJobId(resolved.jobId);
        applyPhase2State(resolved.state);
        setTargets((resolved.state.targets as TargetOption[]) || []);
        setParams(
          {
            job_id: resolved.jobId,
            target: resolved.target || effectiveTarget,
          },
          { replace: true },
        );
        setPageGate("ready");
        return;
      }

      setPageGate("no_job");
    };

    void bootstrap();
    return () => {
      cancelled = true;
      stopPoll();
    };
  }, [
    runParam,
    jobParam,
    effectiveTarget,
    targetParam,
    deskTarget,
    loadJobContext,
    applyPhase2State,
    setParams,
    toast,
  ]);

  const switchTarget = async (targetName: string, targetJobId?: string) => {
    if (targetJobId) {
      setParams({ job_id: targetJobId });
      await loadPhase2State(targetJobId);
      return;
    }
    const data = await apiGet<{ state: Phase2State }>(
      `/api/phase2/state?target=${encodeURIComponent(targetName)}`,
    );
    const jid = jobIdFromPhase2State(data.state);
    if (jid) {
      setParams({ job_id: jid, target: data.state.target_company || targetName });
      applyPhase2State(data.state);
    }
  };

  const pollExtraction = useCallback(
    (runId: string) => {
      if (!runId) return;
      extractRunIdRef.current = runId;
      setExtractRunId(runId);
      stopPoll();
      setExtracting(true);

      const tick = async () => {
        try {
          const data = await apiGet<Phase1JobPoll>(
            `/api/jobs/${encodeURIComponent(runId)}?lite=true`,
          );
          setExtractProgress(data.progress || null);
          if (data.status === "running" || data.status === "pending") {
            setJobsRefreshToken((t) => t + 1);
            return;
          }
          setExtractProgress(null);
          stopPoll();
          setExtracting(false);
          extractRunIdRef.current = "";
          setExtractRunId("");
          setJobsRefreshToken((t) => t + 1);
          if (data.status === "ok" && data.state) {
            applyPhase2State(data.state as Phase2State);
            toast("Extraction complete — see Logs for per-page detail");
          } else if (data.status === "cancelled") {
            toast("Extraction stopped — see Logs for detail");
          } else if (data.status === "budget_exceeded") {
            toast(data.error || "Extraction stopped — job budget exceeded", true);
          } else {
            toast(data.error || "Extraction failed", true);
          }
          if (jobId) {
            await loadPhase2State(jobId);
            void refreshSpend();
          }
        } catch (e) {
          stopPoll();
          setExtracting(false);
          toast(formatApiError(e), true);
        }
      };

      void tick();
      pollRef.current = window.setInterval(() => void tick(), 3500);
    },
    [applyPhase2State, jobId, loadPhase2State, refreshSpend, toast],
  );

  const handleActiveExtraction = useCallback(
    (runId: string) => {
      if (!runId) return;
      if (extractRunIdRef.current === runId && pollRef.current) return;
      pollExtraction(runId);
    },
    [pollExtraction],
  );

  const mergeContractor = async (fromName: string, toName: string) => {
    const from = fromName.trim();
    const to = toName.trim();
    if (!jobId || !from || !to) {
      toast("Choose or type a canonical contractor name", true);
      return;
    }
    if (from.toLowerCase() === to.toLowerCase()) {
      toast("Source and target names are the same", true);
      return;
    }
    setContractorActionBusy(true);
    try {
      const data = await apiPost<{
        merge?: { to_name?: string; evidence_updated?: number };
        state: Phase2State;
      }>(`/api/phase2/jobs/${encodeURIComponent(jobId)}/contractors/merge`, {
        from,
        to,
      });
      applyPhase2State(data.state);
      const keep = data.merge?.to_name || to;
      setSelected(keep);
      setMergeIntoSelect("");
      setMergeIntoCustom("");
      toast(
        `Merged into ${keep} (${data.merge?.evidence_updated ?? 0} page(s) updated)`,
      );
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setContractorActionBusy(false);
    }
  };

  const deleteContractor = async (name: string) => {
    const contractor = name.trim();
    if (!jobId || !contractor) return;
    const count =
      onTargetContractorGroups.find((g) => g.name === contractor)?.source_count ?? 0;
    const ok = window.confirm(
      `Remove "${contractor}" from this job? This clears it from ${count} source page(s).`,
    );
    if (!ok) return;
    setContractorActionBusy(true);
    try {
      const data = await apiPost<{
        delete?: { evidence_updated?: number };
        state: Phase2State;
      }>(`/api/phase2/jobs/${encodeURIComponent(jobId)}/contractors/delete`, {
        name: contractor,
      });
      applyPhase2State(data.state);
      toast(
        `Removed ${contractor} (${data.delete?.evidence_updated ?? 0} page(s) updated)`,
      );
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setContractorActionBusy(false);
    }
  };

  const runExtract = async () => {
    if (!jobId || !model) {
      toast("Select extraction model", true);
      return;
    }
    try {
      const start = await apiPost<{ run_id: string; log_url?: string }>(
        `/api/phase2/jobs/${encodeURIComponent(jobId)}/extract-contractors`,
        { model, force },
      );
      const logPath = start.log_url || (start.run_id ? `/logs?run=${start.run_id}` : "");
      setExtractLogUrl(logPath);
      toast("Extraction started — open Logs to follow each page");
      if (start.run_id) pollExtraction(start.run_id);
      setJobsRefreshToken((t) => t + 1);
    } catch (e) {
      toast(formatApiError(e), true);
    }
  };

  const onTargetContractorGroups = useMemo(() => {
    return (state?.contractor_groups || [])
      .map((g) => {
        const sources = dedupeContractorSources(
          (g.sources || []).filter((s) => s.status !== "rejected_no_target"),
        );
        return { ...g, sources, source_count: sources.length };
      })
      .filter((g) => (g.sources?.length ?? 0) > 0);
  }, [state?.contractor_groups]);

  useEffect(() => {
    setMergeIntoSelect("");
    setMergeIntoCustom("");
  }, [selected, jobId]);

  const phase3Href = (opts?: { bucketId?: string }) => {
    const q = new URLSearchParams();
    if (jobId) q.set("job_id", jobId);
    if (opts?.bucketId) {
      q.set("bucket", opts.bucketId);
    } else {
      const t = targetName === "—" ? "" : targetName;
      if (t) q.set("target", t);
    }
    const qs = q.toString();
    return qs ? `/vendor-clusters?${qs}` : "/vendor-clusters";
  };

  const phase3HrefForGroup = (g: ContractorGroup) =>
    phase3Href({ bucketId: g.bucket_id });

  const goToPhase3ForGroup = (g: ContractorGroup) => {
    navigate(phase3HrefForGroup(g));
  };

  const groups = onTargetContractorGroups.filter((g) =>
    !filter.trim() ? true : g.name.toLowerCase().includes(filter.toLowerCase()),
  );
  const detail = groups.find((g) => g.name === selected);

  const allEvidencePages = useMemo(
    () => state?.evidence || [],
    [state?.evidence],
  );

  const filteredPages = useMemo(() => {
    const q = pageFilter.trim().toLowerCase();
    if (!q) return allEvidencePages;
    return allEvidencePages.filter((p) =>
      `${p.title || ""} ${p.url || ""}`.toLowerCase().includes(q),
    );
  }, [allEvidencePages, pageFilter]);

  const maxPageBatch = Math.max(0, Math.ceil(filteredPages.length / PAGE_BATCH) - 1);
  const safeBatch = Math.min(pageBatch, maxPageBatch);
  const pageSliceStart = safeBatch * PAGE_BATCH;
  const pageSliceEnd = Math.min(pageSliceStart + PAGE_BATCH, filteredPages.length);
  const visiblePages = filteredPages.slice(pageSliceStart, pageSliceEnd);

  const targetName = state?.target_company || "—";
  const ec = state?.extraction_counts || {};
  const offTargetCount =
    state?.excluded_rejected_count ?? ec.excluded_rejected ?? 0;
  const totalEvidence =
    ec.eligible_total ?? state?.evidence_count ?? allEvidencePages.length;
  const pendingExtraction = ec.pending ?? 0;
  const allEligibleExtracted =
    pendingExtraction === 0 && (ec.processed ?? 0) > 0 && !force;

  const extractionModels = useMemo(() => {
    return [...(state?.cheap_models || [])].sort((a, b) =>
      (a.name || a.id).localeCompare(b.name || b.id, undefined, {
        sensitivity: "base",
      }),
    );
  }, [state?.cheap_models]);

  if (runParam) {
    return null;
  }

  if (pageGate === "loading") {
    return (
      <main className="flex-1 flex flex-col items-center justify-center gap-3 min-h-0">
        <span className="orion-spinner w-8 h-8 border-[3px]" aria-hidden />
        <p className="text-body-sm text-on-surface-variant">Loading evidence desk…</p>
      </main>
    );
  }

  if (pageGate === "no_target") {
    return (
      <DeskTargetGate
        phaseNum={2}
        phaseLabel="Evidence"
        title="Main contractor extraction"
        mode="no_target"
      />
    );
  }

  if (pageGate === "no_job") {
    return (
      <DeskTargetGate
        phaseNum={2}
        phaseLabel="Evidence"
        title="Main contractor extraction"
        mode="no_job"
        targetName={pendingTarget || effectiveTarget}
      />
    );
  }

  const pagesMeta =
    filteredPages.length > 0
      ? `Showing ${pageSliceStart + 1}–${pageSliceEnd} of ${filteredPages.length}${
          pageFilter.trim() && filteredPages.length !== allEvidencePages.length
            ? ` (${allEvidencePages.length} on-target)`
            : ""
        }`
      : jobId
        ? "No on-target pages — run Phase 1 on Search"
        : "Select a target";

  const contractorsMeta =
    groups.length > 0
      ? `${groups.length} contractor(s)${selected ? ` · ${selected}` : ""}`
      : "Run extraction to identify main contractors";

  return (
    <>
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col custom-scrollbar">
        <PageTitle
          phaseNum={2}
          phaseLabel="Evidence"
          title="Main contractor extraction"
          meta={
            extracting
              ? "Extraction in progress — open Jobs for run history"
              : "Review scraped pages, then extract contractors — Jobs drawer for extraction runs"
          }
          actions={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setJobsPanelOpen((open) => !open)}
              aria-expanded={jobsPanelOpen}
              aria-controls="phase2-jobs-panel"
              className={jobsPanelOpen ? "text-primary bg-primary/10" : undefined}
            >
              {extracting ? <span className="orion-spinner" aria-hidden /> : null}
              <Icon name="schedule" className="text-base" />
              Jobs
            </Button>
          }
        />

        <Drawer
          id="phase2-jobs-panel"
          open={jobsPanelOpen}
          onClose={() => setJobsPanelOpen(false)}
          title="Extraction jobs"
          subtitle={
            jobId
              ? `${targetName}${jobId ? ` · job ${jobId.slice(0, 8)}…` : ""}`
              : "Select a target / job to load extraction runs"
          }
        >
          <TargetJobRunsPanel
            mode="extraction"
            jobId={jobId}
            targetName={targetName === "—" ? "" : targetName}
            embedded
            fillHeight
            showToggle={false}
            defaultCollapsed={false}
            refreshToken={jobsRefreshToken}
            highlightRunId={extractRunId}
            liveProgress={extracting ? extractProgress : null}
            onOpenRun={(runId) => {
              setJobsPanelOpen(false);
              pollExtraction(runId);
            }}
            onActiveRun={handleActiveExtraction}
          />
        </Drawer>

        <div className="px-6 py-2 border-b border-outline-variant text-body-sm font-data-mono text-on-surface-variant flex flex-wrap gap-x-4 shrink-0">
          <span title={`Pages related to ${targetName}`}>
            Evidence: {totalEvidence}
          </span>
          {offTargetCount > 0 ? (
            <span
              className="text-on-surface-variant/80"
              title="Target not found in page text — excluded from list and counts"
            >
              Off-target (excluded): {offTargetCount}
            </span>
          ) : null}
          <span>Extracted: {ec.processed ?? 0}</span>
          <span>Pending: {ec.pending ?? 0}</span>
          <span>Contractors: {state?.contractor_count ?? groups.length}</span>
        </div>

        <CollapsibleSection
          title="Extract contractors"
          meta={extracting ? "Extraction in progress…" : model ? `Model: ${model.split("/").pop()}` : undefined}
          collapsed={extractCollapsed}
          onToggle={() => setExtractCollapsed((c) => !c)}
        >
          {(extracting || extractLogUrl) && extractRunId ? (
            <div className="px-6 py-2 border-t border-outline-variant/50 bg-primary/5 flex flex-col gap-2 text-body-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-on-surface-variant font-data-mono">
                Phase 2 run <span className="text-primary">{extractRunId}</span>
                {extracting ? " · LLM extraction running…" : " · finished"}
              </span>
              {extractLogUrl ? (
                <Link
                  to={extractLogUrl}
                  className="text-primary font-semibold uppercase tracking-widest text-[10px] hover:underline"
                >
                  Open execution log
                </Link>
              ) : null}
              </div>
              {extracting && extractProgress ? (
                <>
                  <div className="h-1.5 bg-surface-variant rounded overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{
                        width: `${
                          Number(extractProgress.queries_queued) > 0
                            ? Math.min(
                                100,
                                Math.round(
                                  ((Number(extractProgress.queries_done) || 0) /
                                    Number(extractProgress.queries_queued)) *
                                    100,
                                ),
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <p className="font-data-mono text-on-surface-variant truncate">
                    {Number(extractProgress.queries_queued) > 0
                      ? `Page ${extractProgress.queries_done ?? 0} / ${extractProgress.queries_queued} · `
                      : ""}
                    {extractProgress.current_title || extractProgress.current_url || "Starting…"}
                  </p>
                </>
              ) : null}
            </div>
          ) : null}
          <div className="px-6 py-3 flex flex-wrap gap-3 items-end border-t border-outline-variant/50">
            <Field label="Target / job" className="min-w-[240px]">
              <Select
                value={jobId}
                onChange={(e) => {
                  const opt = e.target.selectedOptions[0];
                  const tid = opt.value;
                  const tname = opt.dataset.target || "";
                  if (tid) {
                    setParams({ job_id: tid });
                    loadPhase2State(tid);
                  } else if (tname) switchTarget(tname);
                }}
              >
                <option value="">— Select target —</option>
                {targets.map((t) => (
                  <option
                    key={t.latest_job_id || t.target_display_name}
                    value={t.latest_job_id || ""}
                    data-target={t.target_display_name}
                  >
                    {formatTargetOptionLabel(t)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Model" className="min-w-[200px]">
              <Select value={model} onChange={(e) => setModel(e.target.value)}>
                {extractionModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 self-end">
              <label className="flex items-center gap-1.5 text-body-sm shrink-0 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={force}
                  disabled={spend.desk_locked || extracting}
                  onChange={(e) => setForce(e.target.checked)}
                />
                Force re-process
              </label>
              <CostAcknowledgementGate
                phase="phase2_extract"
                jobId={jobId || undefined}
                model={model}
                force={force}
                estimateKey={`${jobId}-${model}-${force}-${pendingExtraction}`}
                onReadyChange={setCostAckReady}
              />
              <Button
                variant="primary"
                onClick={runExtract}
                disabled={
                  extracting ||
                  !jobId ||
                  spend.desk_locked ||
                  (spend.evidence_cap_reached && !force) ||
                  allEligibleExtracted ||
                  !costAckReady
                }
              >
                {extracting && <span className="orion-spinner" />}
                Extract contractors
              </Button>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Evidence pages"
          meta={pagesMeta}
          collapsed={pagesCollapsed}
          onToggle={() => setPagesCollapsed((c) => !c)}
        >
          <div className="border-t border-outline-variant/50 flex flex-col max-h-[min(42vh,420px)]">
            <div className="px-6 py-2 flex flex-wrap items-center gap-3 border-b border-outline-variant/40">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Icon
                  name="search"
                  className="absolute left-2 top-1.5 text-on-surface-variant text-lg"
                />
                <Input
                  value={pageFilter}
                  onChange={(e) => setPageFilter(e.target.value)}
                  placeholder="Filter pages by title or URL…"
                  className="pl-9 py-1.5"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  disabled={safeBatch <= 0}
                  onClick={() => setPageBatch((b) => Math.max(0, b - 1))}
                >
                  Previous
                </Button>
                <span className="text-body-sm font-data-mono text-on-surface-variant tabular-nums">
                  {filteredPages.length ? `${safeBatch + 1} / ${maxPageBatch + 1}` : "—"}
                </span>
                <Button
                  size="sm"
                  disabled={safeBatch >= maxPageBatch}
                  onClick={() => setPageBatch((b) => Math.min(maxPageBatch, b + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
              <EvidencePageLinks pages={visiblePages} />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Search contractors"
          meta={contractorsMeta}
          collapsed={contractorsCollapsed}
          onToggle={() => setContractorsCollapsed((c) => !c)}
          className={
            contractorsCollapsed
              ? "shrink-0"
              : "flex-1 min-h-0 flex flex-col border-b-0"
          }
        >
          <div className="flex flex-1 min-h-0 panel-border mx-4 mb-4 mt-2 rounded-lg overflow-hidden bg-surface-container-low border border-outline-variant">
            <div className="w-full md:w-[32%] min-w-[220px] border-r border-outline-variant flex flex-col min-h-0">
              <div className="p-3 border-b border-outline-variant shrink-0 space-y-2">
                <div className="relative">
                  <Icon
                    name="search"
                    className="absolute left-2 top-1.5 text-on-surface-variant text-lg"
                  />
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search contractors…"
                    className="pl-9 py-1.5"
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                {groups.length ? (
                  groups.map((g) => (
                    <div
                      key={g.name}
                      className={`flex items-stretch border-b border-outline-variant ${
                        selected === g.name
                          ? "bg-surface-container-high border-l-4 border-l-primary"
                          : "hover:bg-surface-variant border-l-4 border-l-transparent"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelected(g.name)}
                        className="flex-1 text-left px-4 py-3 flex justify-between min-w-0"
                      >
                        <span className="font-data-label text-[11px] uppercase truncate text-primary">
                          {g.name}
                        </span>
                        <span className="text-[10px] text-on-surface-variant shrink-0 ml-2">
                          {g.source_count || 0}
                        </span>
                      </button>
                      {g.bucket_id ? (
                        <button
                          type="button"
                          title="Open in Phase 3 (PDR synthesis)"
                          className="flex items-center px-3 text-tertiary hover:text-primary shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToPhase3ForGroup(g);
                          }}
                        >
                          <Icon name="hub" className="text-lg" />
                        </button>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="p-8 text-center text-body-sm text-on-surface-variant">
                    {jobId
                      ? "No contractors yet — run Extract contractors above."
                      : "Select a job or run Phase 1 on Search."}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar min-h-0">
              {!detail ? (
                <p className="text-body-sm text-on-surface-variant">
                  {groups.length
                    ? "Select a main contractor to view sources."
                    : `Target: ${targetName}. Run extraction to identify main contractors.`}
                </p>
              ) : (
                <div>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <h4 className="font-headline-lg text-headline-lg text-on-surface">{detail.name}</h4>
                      <p className="text-body-sm text-on-surface-variant mt-2 font-data-mono">
                        {(detail.sources || []).length} source(s) · Target {targetName}
                      </p>
                      {detail.aliases?.length ? (
                        <p className="text-[10px] text-on-surface-variant mt-2 font-data-mono">
                          Also known as: {detail.aliases.join(", ")}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={contractorActionBusy}
                        onClick={() => void deleteContractor(detail.name)}
                      >
                        Delete contractor
                      </Button>
                    </div>
                  </div>

                  <div className="mb-6 panel-border rounded-lg p-4 bg-surface-container-high/40">
                    <FieldLabel className="mb-3 opacity-60">Merge or rename</FieldLabel>
                    <p className="text-body-sm text-on-surface-variant mb-3">
                      Combine <strong className="text-primary">{detail.name}</strong> into
                      another contractor. Duplicate source URLs are kept once. Aliases apply
                      to future extractions.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 flex-wrap items-end">
                      <Field label="Merge into existing" className="min-w-[200px] flex-1">
                        <Select
                          value={mergeIntoSelect}
                          onChange={(e) => {
                            setMergeIntoSelect(e.target.value);
                            if (e.target.value) setMergeIntoCustom("");
                          }}
                          disabled={contractorActionBusy}
                        >
                          <option value="">— Select contractor —</option>
                          {groups
                            .filter((g) => g.name !== detail.name)
                            .map((g) => (
                              <option key={g.name} value={g.name}>
                                {g.name} ({g.source_count || 0})
                              </option>
                            ))}
                        </Select>
                      </Field>
                      <Field label="Or canonical name" className="min-w-[200px] flex-1">
                        <Input
                          type="text"
                          value={mergeIntoCustom}
                          onChange={(e) => {
                            setMergeIntoCustom(e.target.value);
                            if (e.target.value.trim()) setMergeIntoSelect("");
                          }}
                          disabled={contractorActionBusy}
                          placeholder="e.g. Google"
                        />
                      </Field>
                      <Button
                        variant="primary"
                        disabled={contractorActionBusy}
                        onClick={() =>
                          void mergeContractor(
                            detail.name,
                            mergeIntoCustom.trim() || mergeIntoSelect,
                          )
                        }
                      >
                        Apply merge
                      </Button>
                    </div>
                  </div>

                  <div>
                    <FieldLabel className="mb-3 opacity-60">Evidence sources</FieldLabel>
                    <div className="space-y-2">
                      {(detail.sources || []).length ? (
                        (detail.sources || []).map((s) => (
                          <a
                            key={s.evidence_id || s.url}
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block panel-border p-3 hover:bg-surface-variant/40"
                          >
                            <span className="text-primary font-data-mono text-body-sm font-semibold">
                              {s.title || s.url}
                            </span>
                            <p className="text-[10px] text-on-surface-variant truncate mt-1">
                              {s.url}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {s.discovery_label ? (
                                <p className="text-[10px] text-secondary uppercase tracking-wide">
                                  {s.discovery_label}
                                </p>
                              ) : null}
                              {formatPublishedDate(s.published_date) ? (
                                <p className="text-[10px] text-on-surface-variant">
                                  {formatPublishedDate(s.published_date)}
                                </p>
                              ) : null}
                            </div>
                          </a>
                        ))
                      ) : (
                        <p className="text-body-sm text-on-surface-variant">No sources linked.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSection>
        </div>
      </main>
    </>
  );
}
